/**
 * Step 2: template picker grid — built-in culture templates + custom ones.
 * Multi-select enabled: brides can fuse multiple cultural templates into a
 * single wedding plan (events/checklist deduplicated downstream).
 */
import React from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { CheckCircle, Calendar, TaskList } from 'iconoir-react';

function TemplateCard({ tpl, isSelected, onClick, custom = false }) {
  const selectedBorder = custom ? 'border-2 border-ochre bg-ochre/5' : 'border-2 border-terracotta bg-terracotta/5';
  const selectedIcon = custom ? 'text-ochre' : 'text-terracotta';
  return (
    <Card
      className={`p-4 cursor-pointer transition-all duration-200 ${
        isSelected
          ? selectedBorder
          : 'border border-border hover:border-border-dark hover:-translate-y-0.5 hover:shadow-sm'
      }`}
      onClick={onClick}
      data-testid={`template-card-${tpl.id}`}
    >
      <div className="flex items-start justify-between mb-1">
        <div className="flex items-center gap-2">
          <h3 className="font-serif text-base text-charcoal">{tpl.name}</h3>
          {custom && <Badge className="bg-ochre/10 text-ochre border-ochre/30 border text-xs">Custom</Badge>}
        </div>
        {isSelected && <CheckCircle className={`w-5 h-5 ${selectedIcon} flex-shrink-0`} />}
      </div>
      <p className="text-xs text-muted mb-2 line-clamp-2">{tpl.description}</p>
      <div className="flex items-center gap-3 text-xs text-muted">
        <span className="flex items-center gap-1">
          <Calendar className="w-3 h-3" /> {tpl.event_count} events
        </span>
        <span className="flex items-center gap-1">
          <TaskList className="w-3 h-3" /> {tpl.checklist_count} tasks
        </span>
      </div>
    </Card>
  );
}

export default function TemplatePickStep({
  templates,
  customTemplates,
  selectedTemplates,
  onSelect,
}) {
  const isSelected = (id) => selectedTemplates.includes(id);
  const count = selectedTemplates.length;
  return (
    <div className="space-y-4 mt-4">
      {count > 0 && (
        <div
          className="flex items-center justify-between rounded-sm border border-terracotta/30 bg-terracotta/5 px-3 py-2"
          data-testid="fusion-selection-summary"
        >
          <span className="text-xs font-mono text-terracotta uppercase tracking-wider">
            {count === 1 ? '1 template selected' : `${count} templates selected — fusion mode`}
          </span>
          <span className="text-xs text-muted">Click a card to toggle</span>
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {templates.map((tpl) => (
          <TemplateCard
            key={tpl.id}
            tpl={tpl}
            isSelected={isSelected(tpl.id)}
            onClick={() => onSelect(tpl.id)}
          />
        ))}
      </div>
      {customTemplates.length > 0 && (
        <>
          <p className="text-xs text-muted uppercase tracking-wider mt-4 mb-2">Your Custom Templates</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {customTemplates.map((tpl) => (
              <TemplateCard
                key={tpl.id}
                tpl={tpl}
                isSelected={isSelected(tpl.id)}
                onClick={() => onSelect(tpl.id)}
                custom
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
